import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Star, Trash2, Eye, Pencil } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { formatFee } from "@/lib/format";

const ALL = "__all__";
const STATUS_COLOR = { published: "bg-green-50 text-green-700", pending: "bg-amber-50 text-amber-700", draft: "bg-slate-100 text-slate-600", rejected: "bg-red-50 text-red-700" };
const LEVELS = ["Undergraduate", "Postgraduate", "Diploma", "Certificate", "Doctorate"];
const MODES = ["Online", "Distance", "Hybrid"];

export default function AdminCourses() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [cats, setCats] = useState([]);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/courses", { params: status ? { status } : {} }).then((r) => setItems(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, [status]);
  useEffect(() => { api.get("/public/filters").then((r) => setCats(r.data.categories || [])).catch(() => {}); }, []);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const preview = (c) => {
    if (c.slug && c.institution_slug) window.open(`/courses/${c.slug}/${c.institution_slug}`, "_blank", "noopener");
    else toast.error("Preview unavailable for this course");
  };

  const setField = (k, v) => setEdit((e) => ({ ...e, [k]: v }));

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/courses/${edit.id}`, {
        course_name: edit.course_name, level: edit.level, category_id: edit.category_id || "",
        description: edit.description || "", duration: edit.duration || "",
        fee: edit.fee === "" || edit.fee == null ? null : Number(edit.fee),
        currency: edit.currency || "INR", eligibility: edit.eligibility || "",
        mode: edit.mode || "Online", admission_cycle: edit.admission_cycle || "",
        course_url: edit.course_url || "", status: edit.status,
      });
      toast.success("Course updated"); setEdit(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Courses</h1>
        <Select value={status || ALL} onValueChange={(v) => setStatus(v === ALL ? "" : v)}>
          <SelectTrigger className="w-40 bg-white" data-testid="admin-course-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All statuses</SelectItem>{["pending", "published", "draft", "rejected"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full min-w-[820px] text-left text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr><th className="p-3">Course</th><th className="p-3">Institution</th><th className="p-3">Category</th><th className="p-3">Fee</th><th className="p-3">Status</th><th className="p-3">Actions</th></tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-t border-slate-100" data-testid={`admin-course-row-${c.id}`}>
                <td className="p-3"><div className="flex items-center gap-2">{c.featured && <Star className="h-4 w-4 fill-sky-500 text-sky-500" />}<span className="font-medium text-slate-900">{c.course_name}</span></div></td>
                <td className="p-3 text-slate-500">{c.institution_name}</td>
                <td className="p-3 text-slate-500">{c.category || "—"}</td>
                <td className="p-3 text-slate-600">{formatFee(c.fee, c.currency)}</td>
                <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_COLOR[c.status]}`}>{c.status}</span></td>
                <td className="p-3">
                  <div className="flex items-center gap-1">
                    <Button size="icon" variant="ghost" title="Preview" onClick={() => preview(c)} data-testid={`preview-course-${c.id}`}><Eye className="h-4 w-4 text-slate-500" /></Button>
                    <Button size="icon" variant="ghost" title="Edit" onClick={() => setEdit({ ...c })} data-testid={`edit-course-${c.id}`}><Pencil className="h-4 w-4 text-blue-700" /></Button>
                    {c.status !== "published" && <Button size="icon" variant="ghost" title="Publish" onClick={() => act(() => api.post(`/admin/courses/${c.id}/approve`), "Published")} data-testid={`approve-course-${c.id}`}><Check className="h-4 w-4 text-green-600" /></Button>}
                    {c.status !== "rejected" && <Button size="icon" variant="ghost" title="Reject" onClick={() => act(() => api.post(`/admin/courses/${c.id}/reject`), "Rejected")}><X className="h-4 w-4 text-red-500" /></Button>}
                    <Button size="icon" variant="ghost" title={c.featured ? "Unfeature" : "Feature"} onClick={() => act(() => api.post(`/admin/courses/${c.id}/featured`, { featured: !c.featured }), "Updated")}><Star className={`h-4 w-4 ${c.featured ? "fill-sky-500 text-sky-500" : "text-slate-400"}`} /></Button>
                    <Button size="icon" variant="ghost" title="Delete" onClick={() => window.confirm("Delete course?") && act(() => api.delete(`/admin/courses/${c.id}`), "Deleted")}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-400">No courses found.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle>Edit Course</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2"><Label className="mb-1.5 block">Course name</Label><Input value={edit.course_name || ""} onChange={(e) => setField("course_name", e.target.value)} data-testid="edit-course-name" /></div>
              <div>
                <Label className="mb-1.5 block">Category</Label>
                <Select value={edit.category_id || ALL} onValueChange={(v) => setField("category_id", v === ALL ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="Category" /></SelectTrigger>
                  <SelectContent><SelectItem value={ALL}>Uncategorised</SelectItem>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block">Level</Label>
                <Select value={edit.level || "Postgraduate"} onValueChange={(v) => setField("level", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block">Duration</Label><Input value={edit.duration || ""} onChange={(e) => setField("duration", e.target.value)} /></div>
              <div>
                <Label className="mb-1.5 block">Mode</Label>
                <Select value={edit.mode || "Online"} onValueChange={(v) => setField("mode", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block">Fee</Label><Input type="number" value={edit.fee ?? ""} onChange={(e) => setField("fee", e.target.value)} /></div>
              <div>
                <Label className="mb-1.5 block">Currency</Label>
                <Select value={edit.currency || "INR"} onValueChange={(v) => setField("currency", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["INR", "USD", "GBP"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block">Admission cycle</Label><Input value={edit.admission_cycle || ""} onChange={(e) => setField("admission_cycle", e.target.value)} /></div>
              <div className="sm:col-span-2"><Label className="mb-1.5 block">Eligibility</Label><Input value={edit.eligibility || ""} onChange={(e) => setField("eligibility", e.target.value)} /></div>
              <div className="sm:col-span-2"><Label className="mb-1.5 block">Course URL</Label><Input value={edit.course_url || ""} onChange={(e) => setField("course_url", e.target.value)} /></div>
              <div className="sm:col-span-2"><Label className="mb-1.5 block">Description</Label><Textarea rows={4} value={edit.description || ""} onChange={(e) => setField("description", e.target.value)} /></div>
              <div>
                <Label className="mb-1.5 block">Status</Label>
                <Select value={edit.status || "draft"} onValueChange={(v) => setField("status", v)}>
                  <SelectTrigger data-testid="edit-course-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{["draft", "pending", "published", "rejected"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button className="bg-blue-800 hover:bg-blue-900" onClick={saveEdit} disabled={saving} data-testid="save-course-edit">{saving ? "Saving..." : "Save Changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
